import { GetRecordByIdResult } from '@indigohive/cogfy-types/endpoints/getRecordById'
import { GetCollectionFieldsResultData } from '@indigohive/cogfy-types/endpoints/getCollectionFields'
import type {
  AuthorRecordProperty,
  ChatRecordProperty,
  CreateDateRecordProperty,
  DateRecordProperty,
  ReferenceRecordProperty,
  UpdateDateRecordProperty,
  UUID,
  VectorRecordProperty
} from '@indigohive/cogfy-types'
import { UpdateRecordPropertiesOperationData } from '@indigohive/cogfy-types/endpoints/transactions'
import { FormattedDate } from '../../components'
import { Link } from 'react-router-dom'
import { useWorkspaceSlug } from '../../hooks'
import { useTranslation } from 'react-i18next'
import {
  BooleanRecordPropertyInput,
  ChatRecordProperyInput,
  FileRecordPropertyInput,
  JsonRecordPropertyInput,
  NumberRecordPropertyInput,
  ScheduleRecordPropertyInput,
  SelectRecordPropertyInput,
  TextRecordPropertyInput
} from './components'
import { HttpResponsePropertyInput } from './components/HttpResponseRecordPropertyInput'

export type RecordPropertyContentProps = {
  collectionId: UUID
  field: GetCollectionFieldsResultData
  record: GetRecordByIdResult
  disabled?: boolean
  stale?: boolean
  error?: boolean
  canEdit?: boolean
  onUpdateRecordProperties?: (data: UpdateRecordPropertiesOperationData) => void
}

function EmptyFallback () {
  const { t } = useTranslation()

  return (
    <span className="opacity-40">
      {t('RecordPage:Empty')}
    </span>
  )
}

export function RecordPropertyContent (props: RecordPropertyContentProps) {
  const { collectionId, field, record, disabled, stale, error, canEdit } = props

  const workspaceSlug = useWorkspaceSlug()

  const property = record.properties[field.id]

  if (!property) {
    return (
      <div className="my-1 text-sm">
        <EmptyFallback />
      </div>
    )
  }

  return (
    <div className="my-1 text-sm max-w-full">
      {field.type === 'text' && (
        <TextRecordPropertyInput
          error={error}
          stale={stale}
          disabled={disabled}
          field={field}
          record={record}
          collectionId={collectionId}
          onUpdateRecordProperties={props.onUpdateRecordProperties}
        />
      )}

      {field.type === 'number' && (
        <NumberRecordPropertyInput
          field={field}
          record={record}
          collectionId={collectionId}
          onUpdateRecordProperties={props.onUpdateRecordProperties}
          disabled={disabled}
          stale={stale}
          error={error}
        />
      )}

      {field.type === 'boolean' && (
        <BooleanRecordPropertyInput
          field={field}
          record={record}
          collectionId={collectionId}
          onUpdateRecordProperties={props.onUpdateRecordProperties}
          disabled={disabled}
        />
      )}

      {field.type === 'json' && (
        <JsonRecordPropertyInput
          field={field}
          record={record}
          collectionId={collectionId}
          onUpdateRecordProperties={props.onUpdateRecordProperties}
          disabled={disabled}
          stale={stale}
          error={error}
        />
      )}

      {field.type === 'date' && (
        <>
          {!(property as DateRecordProperty).date?.value && <EmptyFallback />}
          <FormattedDate
            date={(property as DateRecordProperty).date?.value}
            format={field.data.date?.format ?? 'Pp'}
          />
        </>
      )}

      {field.type === 'select' && (
        <SelectRecordPropertyInput
          field={field}
          record={record}
          collectionId={collectionId}
          onUpdateRecordProperties={props.onUpdateRecordProperties}
          disabled={disabled}
        />
      )}

      {field.type === 'file' && (
        <FileRecordPropertyInput
          collectionId={collectionId}
          field={field}
          record={record}
          canEdit={canEdit}
        />
      )}

      {field.type === 'httpResponse' && (
        <HttpResponsePropertyInput
          field={field}
          record={record}
          disabled={disabled}
          error={error}
        />
      )}

      {field.type === 'reference' && (
        <div>
          {(property as ReferenceRecordProperty)?.reference?.value
            .map(reference => (
              <div key={reference.id} className="py-0.5">
                <Link
                  to={`/${workspaceSlug}/${field.data.reference?.collectionId}/${reference.referencedRecordId}`}
                  className="link link-primary"
                >
                  {reference.title}
                </Link>
              </div>
            ))}
        </div>
      )}

      {field.type === 'author' && (
        (property as AuthorRecordProperty).author?.value?.name
      )}

      {field.type === 'createDate' && (
        <FormattedDate
          date={(property as CreateDateRecordProperty).createDate?.value}
          format={field.data.createDate?.format ?? 'Pp'}
        />
      )}

      {field.type === 'updateDate' && (
        <FormattedDate
          date={(property as UpdateDateRecordProperty).updateDate?.value}
          format={field.data.updateDate?.format ?? 'Pp'}
        />
      )}

      {field.type === 'vector' && (
        (property as VectorRecordProperty).vector?.value?.count ?? <EmptyFallback />
      )}

      {field.type === 'chat' && (
        <ChatRecordProperyInput
          workspace={workspaceSlug}
          collectionId={collectionId}
          field={field}
          record={record}
          property={property as ChatRecordProperty}
        />
      )}

      {field.type === 'schedule' && (
        <ScheduleRecordPropertyInput
          field={field}
          record={record}
          collectionId={collectionId}
          onUpdateRecordProperties={props.onUpdateRecordProperties}
        />
      )}
    </div>
  )
}
